/**
 * Orders API Module - Order and Address Management
 * Extracted from monolithic silo-api.ts for modular architecture
 */

import { type RequestFunction } from './types'
import type {
  Order,
  OrderCreateInput,
  OrderUpdateInput,
  OrderStatus,
  Address,
  AddressCreateInput,
  AddressUpdateInput,
  PaymentMethod,
  Shipping,
  PaginatedResponse,
  FilterParams,
  OrderTimeline,
  OrderHistory,
} from './types'

// Order list params interface for CRM orders
interface OrderListParams {
  limit?: number;
  offset?: number;
  ordering?: string;
  search?: string;
  status?: string;
}

export class OrdersApi {
  constructor(private request: RequestFunction) {}

  // ============================================================================
  // ORDER MANAGEMENT
  // ============================================================================

  async getOrders(params?: FilterParams): Promise<PaginatedResponse<Order>> {
    return this.request('/api/ar/orders/', { params })
  }

  async getOrder(id: number): Promise<Order> {
    return this.request(`/api/ar/orders/${id}/`)
  }

  // CRM-specific order methods (for admin/staff use) - KEEP only line 479 version
  async getCRMOrders(params?: FilterParams): Promise<PaginatedResponse<Order>> {
    // Use the CRM-specific endpoint which provides enhanced data
    return this.request('/api/ar/crm/orders/', { params })
  }

  async getCRMOrder(id: number): Promise<Order> {
    // Use the same endpoint as regular orders - backend handles permissions
    return this.request(`/api/ar/orders/${id}/`)
  }

  async updateCRMOrder(id: number, data: Partial<Order>): Promise<Order> {
    return this.request(`/api/ar/crm/orders/${id}/`, {
      method: 'PATCH',
      body: JSON.stringify(data)
    })
  }

  async updateCRMOrderStatus(id: number, status: string): Promise<Order> {
    return this.request(`/api/ar/crm/orders/${id}/`, {
      method: 'PATCH',
      body: JSON.stringify({ status })
    })
  }

  async createOrder(data: OrderCreateInput): Promise<Order> {
    return this.request('/api/ar/orders/', {
      method: 'POST',
      body: JSON.stringify(data)
    })
  }

  async updateOrder(id: number, data: OrderUpdateInput): Promise<Order> {
    return this.request(`/api/ar/orders/${id}/`, {
      method: 'PATCH',
      body: JSON.stringify(data)
    })
  }

  // ============================================================================
  // ORDER STATUS MANAGEMENT
  // ============================================================================

  async updateOrderStatus(id: number, status: string): Promise<Order> {
    return this.request(`/api/ar/orders/${id}/status/`, {
      method: 'PATCH',
      body: JSON.stringify({ status })
    })
  }

  async updateOrderTracking(id: number, trackingNumber: string, carrier: string): Promise<Order> {
    return this.request(`/api/ar/orders/${id}/tracking/`, {
      method: 'PATCH',
      body: JSON.stringify({ 
        tracking_number: trackingNumber,
        carrier 
      })
    })
  }

  async addOrderNote(id: number, note: string): Promise<Order> {
    return this.request(`/api/ar/orders/${id}/notes/`, {
      method: 'POST',
      body: JSON.stringify({ note })
    })
  }

  async bulkUpdateOrderStatus(ids: number[], status: string): Promise<void> {
    return this.request('/api/ar/orders/bulk-update-status/', {
      method: 'POST',
      body: JSON.stringify({ ids, status })
    })
  }

  async cancelOrder(id: number): Promise<Order> {
    return this.request(`/api/ar/orders/${id}/cancel/`, {
      method: 'POST'
    })
  }

  async completeOrder(id: number): Promise<Order> {
    return this.request(`/api/ar/orders/${id}/complete/`, {
      method: 'POST'
    })
  }

  async approveOrder(id: number): Promise<{
    success: boolean
    order_id: number
    status: string
    message: string
    invoice_created?: boolean
    invoice_id?: string
    invoice_url?: string
  }> {
    return this.request(`/api/ar/orders/${id}/approve/`, {
      method: 'POST',
      body: JSON.stringify({})
    })
  }

  // ============================================================================
  // ORDER TIMELINE & HISTORY
  // ============================================================================

  /**
   * Get comprehensive order activity timeline
   * Returns chronological timeline of all order activities including:
   * - Order creation and status changes
   * - Payment activities
   * - Invoice generation and updates
   * - QuickBooks synchronization
   * - Tracking information
   * - Custom notes and events
   */
  async getOrderTimeline(id: number): Promise<OrderTimeline> {
    return this.request(`/api/ar/orders/${id}/timeline/`)
  }

  /**
   * Get order history including status changes and notes
   */
  async getOrderHistory(id: number): Promise<OrderHistory> {
    return this.request(`/api/ar/orders/${id}/history/`)
  }

  /**
   * Generate and download PDF invoice for an order
   */
  async getOrderInvoicePDF(id: number): Promise<Blob> {
    // For PDF downloads, we need to handle this differently
    // The request function returns JSON, but PDFs need special handling
    // This would need to be implemented with a direct fetch call
    // For now, return a placeholder implementation
    throw new Error('PDF download not yet implemented - requires special blob handling')
  }

  // ============================================================================
  // PAYMENT & SHIPPING METHODS
  // ============================================================================

  async getPaymentMethods(): Promise<Array<{id: number, name: string, process_at_checkout: boolean}>> {
    return this.request('/api/ar/payment-methods/')
  }

  async getShippingMethods(): Promise<Array<{id: number, size: string, weight: number, speed: string}>> {
    return this.request('/api/ar/shipping-methods/')
  }

  // ============================================================================
  // ORDER CREATION HELPER
  // ============================================================================

  /**
   * Order Creation Helper - NEW AR API endpoint
   * Returns all data needed for order creation including customer_id
   */
  async getOrderCreationHelper(): Promise<{
    customer_id: number
    payment_methods: Array<{id: number, name: string, process_at_checkout: boolean}>
    shipping_methods: Array<{id: number, size: string, weight: number, speed: string}>
    saved_addresses: Array<{id: number, address: string, city: string, state: string, zipcode: string, name: string, phone: string}>
    example_payload: any
  }> {
    return this.request('/api/ar/order-creation-helper/')
  }

  // ============================================================================
  // USER-SPECIFIC ORDER METHODS
  // ============================================================================

  /**
   * Get orders for regular users - automatically filtered to their orders
   */
  async getUserOrders(params?: FilterParams): Promise<PaginatedResponse<Order>> {
    return this.request('/api/ar/orders/', { params })
  }

  // ============================================================================
  // ADDRESS MANAGEMENT (Portal)
  // ============================================================================

  async getAddresses(params?: any): Promise<PaginatedResponse<Address>> {
    return this.request('/api/ar/orders/address/', { params })
  }

  async getAddress(id: number): Promise<Address> {
    return this.request(`/api/ar/orders/address/${id}/`)
  }

  async createAddress(data: AddressCreateInput): Promise<Address> {
    return this.request('/api/ar/orders/address/', {
      method: 'POST',
      body: JSON.stringify(data)
    })
  }

  async updateAddress(id: number, data: AddressUpdateInput): Promise<Address> {
    return this.request(`/api/ar/orders/address/${id}/`, {
      method: 'PATCH',
      body: JSON.stringify(data)
    })
  }

  async deleteAddress(id: number): Promise<void> {
    // Use archive endpoint instead of delete for soft deletion
    await this.request(`/api/ar/orders/address/${id}/archive/`, {
      method: 'POST',
      body: JSON.stringify({})
    })
  }

  async setDefaultAddress(id: number): Promise<Address> {
    return this.request(`/api/ar/orders/address/${id}/set-default/`, {
      method: 'POST'
    })
  }

  async archiveAddress(id: number): Promise<{ message: string; archived_at: string; address_id: number }> {
    return this.request(`/api/ar/orders/address/${id}/archive/`, {
      method: 'POST'
    })
  }

  async restoreAddress(id: number): Promise<Address> {
    return this.request(`/api/ar/orders/address/${id}/restore/`, {
      method: 'POST'
    })
  }

  // Alias for getAddresses to match the account page usage
  async getUserAddresses(params?: any): Promise<PaginatedResponse<Address>> {
    return this.getAddresses(params)
  }

  // ============================================================================
  // ADMIN ORDER MANAGEMENT
  // ============================================================================

  async getOrdersAdmin(params?: FilterParams): Promise<PaginatedResponse<Order>> {
    const response = await this.request<Order[] | PaginatedResponse<Order>>('/api/ar/orders/', { params })
    
    // Handle both array and paginated response formats
    if (Array.isArray(response)) {
      return {
        count: response.length,
        next: null,
        previous: null,
        total_pages: 1,
        current_page: 1,
        page_size: response.length,
        results: response
      }
    }
    
    return response
  }

  async getOrderAdmin(id: number): Promise<Order> {
    return this.request(`/api/ar/orders/${id}/`)
  }

  async createOrderAdmin(data: OrderCreateInput): Promise<Order> {
    return this.request('/api/ar/orders/', {
      method: 'POST',
      body: JSON.stringify(data)
    })
  }

  async updateOrderAdmin(id: number, data: OrderUpdateInput): Promise<Order> {
    return this.request(`/api/ar/orders/${id}/`, {
      method: 'PATCH',
      body: JSON.stringify(data)
    })
  }

  async updateOrderStatusAdmin(id: number, status: string): Promise<Order> {
    return this.request(`/api/ar/orders/${id}/status/`, {
      method: 'PATCH',
      body: JSON.stringify({ status })
    })
  }

  async updateOrderTrackingAdmin(id: number, trackingNumber: string): Promise<Order> {
    return this.request(`/api/ar/orders/${id}/tracking/`, {
      method: 'PATCH',
      body: JSON.stringify({ 
        tracking_number: trackingNumber 
      })
    })
  }

  async addOrderNoteAdmin(id: number, text: string): Promise<Order> {
    return this.request(`/api/ar/orders/${id}/notes/`, {
      method: 'PATCH',
      body: JSON.stringify({ notes: text })
    })
  }

  async cancelOrderAdmin(id: number, reason?: string): Promise<Order> {
    return this.request(`/api/ar/orders/${id}/cancel/`, {
      method: 'POST',
      body: JSON.stringify({ reason })
    })
  }

  async completeOrderAdmin(id: number): Promise<Order> {
    return this.request(`/api/ar/orders/${id}/complete/`, {
      method: 'POST',
      body: JSON.stringify({})
    })
  }

  async getPaymentMethodsAdmin(): Promise<Array<{id: number, name: string}>> {
    return this.request('/api/ar/payment-methods/')
  }

  async getShippingMethodsAdmin(): Promise<Array<{id: number, name: string}>> {
    return this.request('/api/ar/shipping-methods/')
  }

  async getRecentOrdersAdmin(limit: number = 10): Promise<Order[]> {
    const thirtyDaysAgo = new Date()
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30)
    
    const params: FilterParams = {
      page: 1,
      limit,
      date_from: thirtyDaysAgo.toISOString().split('T')[0],
      ordering: '-timestamp'
    }
    
    const response = await this.getOrdersAdmin(params)
    return response.results
  }

  // ============================================================================
  // BATCH OPERATIONS (Admin-specific)
  // ============================================================================

  async bulkUpdateOrderStatusAdmin(orderIds: number[], status: string): Promise<{ updated_count: number }> {
    return this.request('/api/ar/orders/bulk-update-status/', {
      method: 'POST',
      body: JSON.stringify({
        order_ids: orderIds,
        status
      })
    })
  }

  // ============================================================================
  // INVOICE OPERATIONS
  // ============================================================================

  /**
   * Get invoice details for an order
   */
  async getOrderInvoice(id: number): Promise<{
    order_id: number;
    order_number: number;
    invoice_id: string | null;
    invoice_url: string | null;
    synced_at: string | null;
    status: string;
    total: string;
    customer: { name: string; email: string };
    items: Array<{ name: string; quantity: number; price: string; total: string }>;
  }> {
    return this.request(`/api/ar/orders/${id}/invoice/`)
  }

  /**
   * Send invoice via email to customer
   */
  async sendOrderInvoice(id: number): Promise<{
    success: boolean;
    message: string;
    order_id: number;
    invoice_id: string;
  }> {
    return this.request(`/api/ar/orders/${id}/invoice/send/`, {
      method: 'POST'
    })
  }

  /**
   * Update payment status for an order
   */
  async updatePaymentStatus(
    id: number,
    data: {
      payment_status: 'paid' | 'partial' | 'pending' | 'overdue';
      payment_date?: string;
      payment_method?: string;
      payment_reference?: string;
    }
  ): Promise<{
    success: boolean;
    order_id: number;
    payment_status: string;
    order_status: string;
    message: string;
  }> {
    return this.request(`/api/ar/orders/${id}/payment-status/`, {
      method: 'POST',
      body: JSON.stringify(data)
    })
  }

  /**
   * Sync order with QuickBooks
   */
  async syncWithQuickBooks(id: number): Promise<{
    success: boolean;
    invoice_id?: string;
    invoice_url?: string;
    error?: string;
  }> {
    return this.request(`/api/ar/orders/${id}/sync_quickbooks/`, {
      method: 'POST'
    })
  }

  /**
   * Get order activities/events
   */
  async getOrderActivities(id: number): Promise<Array<{
    timestamp: string;
    event_type: string;
    description: string;
    user: string;
    metadata?: Record<string, unknown>;
  }>> {
    return this.request(`/api/ar/orders/${id}/activities/`)
  }
}